import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, ArrowLeft, MessageSquare, Mic, MicOff, Send, ChevronRight, CheckCircle2, Star, Brain, RotateCcw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: 'General WFH Interview' },
  { value: 'customer-service', label: 'Customer Service' },
  { value: 'data-entry', label: 'Data Entry' },
  { value: 'tech-support', label: 'Tech Support' },
  { value: 'sales', label: 'Sales' },
  { value: 'admin', label: 'Admin / Virtual Assistant' },
];

export default function PracticeInterview() {
  const { user } = useAuth();
  const [category, setCategory] = useState('general');
  const [mode, setMode] = useState('text'); // 'text' or 'voice'
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(5);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [history, setHistory] = useState([]);
  const [allFeedback, setAllFeedback] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [allAnswers, setAllAnswers] = useState([]);
  const [error, setError] = useState('');
  // Voice
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/interview/history`, { withCredentials: true })
        .then(r => setHistory(r.data.sessions)).catch(() => {});
    }
  }, [user]);

  const startInterview = async () => {
    setError(''); setLoading(true); setCompleted(false);
    setAllFeedback([]); setAllQuestions([]); setAllAnswers([]);
    try {
      const { data } = await axios.post(`${API}/interview/start`, { category }, { withCredentials: true });
      setSessionId(data.session_id);
      setCurrentQuestion(data.question);
      setQuestionIndex(data.question_index);
      setTotalQuestions(data.total_questions);
      setAllQuestions([data.question]);
      setFeedback(''); setAnswer('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start interview');
    }
    setLoading(false);
  };

  const submitAnswer = async () => {
    const ans = mode === 'voice' ? transcript : answer;
    if (!ans.trim()) return;
    setLoading(true); setFeedback('');
    try {
      const { data } = await axios.post(`${API}/interview/${sessionId}/answer`, { answer: ans }, { withCredentials: true });
      setFeedback(data.feedback);
      setAllFeedback(prev => [...prev, data.feedback]);
      setAllAnswers(prev => [...prev, ans]);
      if (data.completed) {
        setCompleted(true);
      } else {
        setCurrentQuestion(data.next_question);
        setQuestionIndex(data.next_index);
        setAllQuestions(prev => [...prev, data.next_question]);
      }
      setAnswer(''); setTranscript('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit answer');
    }
    setLoading(false);
  };

  const nextQuestion = () => {
    setFeedback('');
  };

  // Voice recording
  const startRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Speech recognition not supported in this browser. Try Chrome.');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      let text = '';
      for (let i = 0; i < event.results.length; i++) {
        text += event.results[i][0].transcript;
      }
      setTranscript(text);
    };
    recognition.onerror = (e) => { console.error(e); setIsRecording(false); };
    recognition.onend = () => setIsRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
    setTranscript('');
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/tools" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8">
        <Link to="/tools" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> WFH Tools</Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#047857] flex items-center justify-center">
            <Brain size={22} className="text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-3xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>AI Practice Interview</h1>
            <p className="text-[#1C2A24]/60 text-sm">Practice with AI-powered feedback. Text or voice — your choice!</p>
          </div>
        </div>

        {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-2xl text-sm mb-4" data-testid="interview-error">{error}</div>}

        {/* Not started yet */}
        {!sessionId && !completed && (
          <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="interview-setup">
            <h2 className="text-xl font-medium mb-6" style={{fontFamily:'Outfit'}}>Set Up Your Practice Interview</h2>

            <div className="space-y-5">
              <div>
                <label className="text-sm text-[#1C2A24]/70 mb-2 block">Job Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8]" data-testid="interview-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm text-[#1C2A24]/70 mb-2 block">Answer Mode</label>
                <div className="flex gap-3">
                  <button onClick={() => setMode('text')} data-testid="mode-text"
                    className={`flex-1 py-3 px-4 rounded-2xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${mode === 'text' ? 'bg-[#047857] text-white border-[#047857]' : 'bg-[#F4F1E8] border-[#E5E0D8]'}`}>
                    <MessageSquare size={16} /> Type Answers
                  </button>
                  <button onClick={() => setMode('voice')} data-testid="mode-voice"
                    className={`flex-1 py-3 px-4 rounded-2xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${mode === 'voice' ? 'bg-[#D97757] text-white border-[#D97757]' : 'bg-[#F4F1E8] border-[#E5E0D8]'}`}>
                    <Mic size={16} /> Speak Answers
                  </button>
                </div>
              </div>

              <div className="bg-[#F4F1E8]/50 rounded-2xl p-4">
                <p className="text-sm text-[#1C2A24]/60">You'll answer <strong>5 interview questions</strong> — a mix of general WFH and {category !== 'general' ? `${CATEGORIES.find(c => c.value === category)?.label} ` : ''}role-specific questions. AI will give feedback on each answer.</p>
                {user?.membership === 'free' && <p className="text-xs text-[#D97757] mt-2">Free plan: 3 practice sessions available. Upgrade for unlimited.</p>}
              </div>

              <Button onClick={startInterview} disabled={loading} className="w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12" data-testid="start-interview-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : 'Start Practice Interview'}
              </Button>
            </div>

            {/* History */}
            {history.length > 0 && (
              <div className="mt-8 pt-6 border-t border-[#E5E0D8]">
                <h3 className="text-sm font-medium mb-3" style={{fontFamily:'Outfit'}}>Previous Sessions</h3>
                <div className="space-y-2">
                  {history.slice(0, 5).map(s => (
                    <div key={s.id} className="bg-[#F4F1E8]/50 rounded-xl p-3 flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium capitalize">{s.category.replace('-', ' ')}</span>
                        <p className="text-xs text-[#1C2A24]/40">{new Date(s.created_at).toLocaleDateString()} &middot; {s.answers?.length || 0} questions</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'completed' ? 'bg-[#047857]/10 text-[#047857]' : 'bg-[#D97757]/10 text-[#D97757]'}`}>
                        {s.status === 'completed' ? 'Completed' : 'In Progress'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Active Interview */}
        {sessionId && !completed && (
          <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="active-interview">
            {/* Progress */}
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm text-[#1C2A24]/50">Question {questionIndex + 1} of {totalQuestions}</span>
              <div className="w-32 h-2 bg-[#F4F1E8] rounded-full overflow-hidden">
                <div className="h-full bg-[#047857] rounded-full transition-all" style={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }} />
              </div>
            </div>

            {/* Question */}
            <div className="bg-[#A7F3D0]/20 rounded-2xl p-5 mb-6 border border-[#047857]/10">
              <p className="text-xs text-[#047857] font-medium mb-2">Interview Question:</p>
              <p className="text-lg font-medium text-[#1C2A24]" style={{fontFamily:'Outfit'}} data-testid="interview-question">{currentQuestion}</p>
            </div>

            {/* Answer Area */}
            {!feedback ? (
              <>
                {mode === 'text' ? (
                  <Textarea value={answer} onChange={e => setAnswer(e.target.value)}
                    placeholder="Type your answer here... Be specific and give examples when possible."
                    rows={5} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mb-4" data-testid="interview-answer" />
                ) : (
                  <div className="mb-4">
                    <div className="bg-[#F4F1E8] rounded-2xl p-5 min-h-[120px] mb-3">
                      {transcript ? (
                        <p className="text-sm text-[#1C2A24]" data-testid="voice-transcript">{transcript}</p>
                      ) : (
                        <p className="text-sm text-[#1C2A24]/30 text-center py-6">
                          {isRecording ? 'Listening... Speak your answer clearly.' : 'Click the microphone to start recording your answer.'}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-center">
                      {!isRecording ? (
                        <Button onClick={startRecording} className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white h-14 w-14 p-0" data-testid="start-recording-btn">
                          <Mic size={24} strokeWidth={1.5} />
                        </Button>
                      ) : (
                        <Button onClick={stopRecording} className="rounded-full bg-red-500 hover:bg-red-600 text-white h-14 w-14 p-0 animate-pulse" data-testid="stop-recording-btn">
                          <MicOff size={24} strokeWidth={1.5} />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <Button onClick={submitAnswer} disabled={loading || (mode === 'text' ? !answer.trim() : !transcript.trim())}
                  className="w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12" data-testid="submit-answer-btn">
                  {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> Getting AI Feedback...</> : <><Send size={16} className="mr-2" /> Submit Answer</>}
                </Button>
              </>
            ) : (
              /* Feedback */
              <div data-testid="interview-feedback">
                <div className="bg-[#F4F1E8]/50 rounded-2xl p-5 mb-4 border border-[#E5E0D8]">
                  <p className="text-xs text-[#D97757] font-medium mb-2 flex items-center gap-1"><Star size={12} /> AI Feedback:</p>
                  <p className="text-sm text-[#1C2A24]/80 leading-relaxed whitespace-pre-line">{feedback}</p>
                </div>
                <Button onClick={nextQuestion} className="w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12" data-testid="next-question-btn">
                  Next Question <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Completed */}
        {completed && (
          <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="interview-complete">
            <div className="text-center mb-6">
              <CheckCircle2 size={48} className="text-[#047857] mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="text-xl font-medium" style={{fontFamily:'Outfit'}}>Practice Interview Complete!</h2>
              <p className="text-sm text-[#1C2A24]/60 mt-1">You answered {allAnswers.length} questions. Here's your full review:</p>
            </div>

            {/* Full Review */}
            <div className="space-y-4 mb-6">
              {allQuestions.map((q, i) => (
                <div key={i} className="bg-[#F4F1E8]/50 rounded-2xl p-5">
                  <p className="text-xs text-[#047857] font-medium mb-1">Q{i + 1}: {q}</p>
                  <p className="text-sm text-[#1C2A24]/70 mb-2 italic">Your answer: {allAnswers[i]?.substring(0, 150)}{allAnswers[i]?.length > 150 ? '...' : ''}</p>
                  <div className="bg-white rounded-xl p-3 border border-[#E5E0D8]">
                    <p className="text-xs text-[#D97757] font-medium mb-1">Feedback:</p>
                    <p className="text-xs text-[#1C2A24]/60 whitespace-pre-line">{allFeedback[i]}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button onClick={() => { setSessionId(null); setCompleted(false); setFeedback(''); }} className="flex-1 rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12" data-testid="practice-again-btn">
                <RotateCcw size={16} className="mr-2" /> Practice Again
              </Button>
              <Link to="/jobs" className="flex-1">
                <Button variant="outline" className="w-full rounded-full h-12 border-[#E5E0D8]">Start Applying</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
